import { cn } from '@bw/core';
import { Image } from '@bw/elements';
import { Block } from '../../factories/types';
import { Form } from '../../forms/Form';
import { annotate } from '../../utils/annotate';
import { schemeToClass } from '../../utils/colorMap';
import { imagePositionToClassMap } from '../../utils/imagePositionMap';
import { RichText } from '../../utils/rich-text/RichText';
import { sizeToClassMap } from '../../utils/sizeMap';
import { ButtonWrapper } from '../button/ButtonWrapper';
import styles from './ContentSection.module.scss';

// TODO(amatlash): add AOS fade efeects
// TODO(amatalsh): move to static
const sunburstAsset =
	'https://a-us.storyblok.com/f/1019983/200x450/d277372237/burst-hero.webp';

export const ContentSection: Block<'contentSection'> = ({
	data,
	index: sectionIndex,
}) => {
	const {
		fields: {
			tagLine,
			title,
			text,
			buttons,
			image,
			form,
			addSunburstIcon, // TODO(amatlash): implement sunburst
			colorScheme = 'Default (white)',
			imagePosition = 'Right',
			size = 'Medium',
		},
	} = data;

	const hasImage = !!image;
	const hasForm = !!form;
	const hasOnlyForm = !hasImage && hasForm;
	const hasImageAndForm = hasImage && hasForm;
	const hasImageOrForm = hasImage || hasForm;
	
	const hasButtons = !!buttons?.length;

	const imageDimensions = image?.fields.file?.details.image;
	
	return (
		<section
			className={cn(
				styles.contentSection,
				styles[sizeToClassMap[size]],
				schemeToClass(colorScheme)
			)}
			{...annotate(data, '.')}
		>
			{hasImage ? (
				<div
					className={cn(
						styles.imageWrapper,
						styles[imagePositionToClassMap[imagePosition]]
					)}
					{...annotate(data, 'image')}
				>
					{image.fields.file?.url ? (
						<Image
							src={image.fields.file.url}
							alt=""
							width={imageDimensions?.width || 1000}
							height={imageDimensions?.height || 1000}
							sizes='(max-width: 1023px) 100vw, 49vw'
							priority={sectionIndex === 0}
						/>
					) : null}
				</div>
			) : null}

			<div
				className={cn(
					styles.textContentWrapper,
					hasImageOrForm ? styles.column : styles.solo
				)}
			>
				{tagLine ? (
					<span className={styles.tagline} {...annotate(data, 'tagLine')}>
						{tagLine}
					</span>
				) : null}

				{title ? (
					<div className={styles.title} {...annotate(data, 'title')}>
						<RichText document={title} />
					</div>
				) : null}

				{text ? (
					<div
						className={cn(styles.text, addSunburstIcon && styles.accentBullets)}
						{...annotate(data, 'text')}
					>
						<RichText document={text} />
					</div>
				) : null}

				{hasButtons ? (
					<div className={styles.buttonsWrapper} {...annotate(data, 'buttons')}>
						{buttons.map((button, index) =>
							button ? (
								<ButtonWrapper
									key={button.sys.id}
									data={button}
									isPrimary={index === 0}
								/>
							) : null
						)}
					</div>
				) : null}

				{hasImageAndForm ? (
					<Form name={form} className={cn(styles.form, styles.inTextContent)} />
				) : null}
			</div>

			{hasOnlyForm ? (
				<Form
					name={form}
					className={cn(styles.form, styles.standaloneColumn)}
				/>
			) : null}

			{!hasImageOrForm && addSunburstIcon ? (
				<div className={styles.sunburst}>
					<Image
						alt=""
						src={sunburstAsset}
						width={200}
						height={450}
						{...annotate(data, 'addSunburstIcon')}
					/>
				</div>
			) : null}
		</section>
	);
};
